<template>
  <div class="full-height">
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div
        v-if="isSuccess"
        :class="{ 'full-height': !items.length }"
      >
        <div v-if="items.length" class="table">
          <div class="table__status">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber}}</strong> {{ $t('Common.of') }} <strong>{{ pagination.totalElements | formatNumber }}</strong> {{ $t(`${namespace}.companiesGroup`) }}
          </div>

          <v-layout class="table__header" align-center>
            <v-flex
              v-if="hasRole('companyGroup', 'ROLE_PUT_COMPANYGROUP') || hasRole('companyGroup', 'ROLE_DELETE_COMPANYGROUP')"
              xs1
            />
            <v-flex xs1>ID</v-flex>
            <v-flex xs5>{{ $t(`${namespace}.companyGroupName`) }}</v-flex>
            <v-flex xs5>{{ $t(`${namespace}.companiesCount`) }}</v-flex>
          </v-layout>

          <v-list subheader two-line class="table__body">
            <div v-for="(item, i) in items" :key="`tr-${i}`">
              <v-list-tile
                class="table__body__tr"
                :class="{ 'table__body__tr--active': activateLine(item.companyGroup) }"
                @click="handleClick($event, item)"
              >
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center>
                      <v-flex
                        v-if="hasRole('companyGroup', 'ROLE_PUT_COMPANYGROUP') || hasRole('companyGroup', 'ROLE_DELETE_COMPANYGROUP')"
                        xs1
                      >
                        <v-tooltip
                          v-if="hasRole('companyGroup', 'ROLE_PUT_COMPANYGROUP')"
                          slot="append"
                          min-width="100"
                          max-width="100"
                          top
                        >
                          <v-btn
                            color="gray-6"
                            class="mx-0"
                            slot="activator"
                            flat
                            icon
                            @click="handleEditCompanyGroup(item)"
                          >
                            <v-icon>edit</v-icon>
                          </v-btn>
                          <span>{{ $t(`${namespace}.editCompanyGroup`) }}</span>
                        </v-tooltip>

                        <v-tooltip
                          v-if="hasRole('companyGroup', 'ROLE_DELETE_COMPANYGROUP')"
                          slot="append"
                          min-width="178"
                          max-width="178"
                          top
                        >
                          <v-btn
                            color="gray-6"
                            class="mx-0"
                            slot="activator"
                            flat
                            icon
                            @click="handleRemoveCompanyGroup(item.companyGroup)"
                          >
                            <v-icon>delete</v-icon>
                          </v-btn>
                          <span>{{ $t(`${namespace}.removeCompanyGroup`) }}</span>
                        </v-tooltip>
                      </v-flex>

                      <v-flex xs1>
                        <span :title="item.companyGroup.id" class="text--ellipsis" contenteditable>
                          {{ item.companyGroup.id }}
                        </span>
                      </v-flex>
                      <v-flex xs5>
                        <span class="text--ellipsis" :title="item.companyGroup.name">
                          {{ item.companyGroup.name | ellipsis(40) }}
                        </span>
                      </v-flex>
                      <v-flex xs5>
                        <span class="text--ellipsis" :title="item.companiesTotal">
                          {{ item.companiesTotal | formatNumber }} {{ $t(`${namespace}.companies`) }}
                        </span>
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />
            </div>
          </v-list>
        </div>

        <EmptyState
          v-else
          image="cost-center"
          :text="$t(`${namespace}.noneFound`)"
          style="padding-bottom: 120px"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          style="margin-bottom: 120px"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>

    <RemoveCompanyGroupDialog
      :visible="removeCompanyGroupDialog"
      :companyGroup="companyGroup"
      @close="closeRemoveCompanyGroupDialog"
    />

    <GlobalDrawer
      :show="globalDrawer.visibility"
      :drawerType="globalDrawer.type"
      :data="globalDrawer.data"
      :filters="globalDrawer.filters"
      @close="closeGlobalDrawer"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { hasRole, formatNumber } from 'utils/functions'
import EmptyState from 'components/EmptyState'
import RemoveCompanyGroupDialog from './RemoveCompanyGroupDialog'
import GlobalDrawer from 'components/globalDrawer'

export default {
  name: 'Table',
  props: {
    items: {
      type: Array,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    },
    activeTab: {
      type: Number
    }
  },
  components: {
    EmptyState,
    RemoveCompanyGroupDialog,
    GlobalDrawer
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany,
      claims: ({ userClaims }) => userClaims.claims
    }),

    routeId () {
      const { id } = this.$route.params
      return id ? Number(id) : null
    },

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.CompanyPage.companiesGroup',
      removeCompanyGroupDialog: false,
      companyGroup: null,
      selectedItem: null,
      selectedItems: [],
      globalDrawer: {
        visibility: false,
        type: '',
        data: {},
        filters: {}
      }
    }
  },
  methods: {
    hasRole (group, role) {
      return hasRole(group, role)
    },

    openGlobalDrawer ({ data = {}, filters = {} }) {
      this.globalDrawer = {
        visibility: true,
        type: 'companyGroup',
        data
      }
    },

    closeGlobalDrawer (reload = null) {
      this.globalDrawer = {
        visibility: false,
        type: '',
        data: {},
        filters: {}
      }

      this.selectedItem = null
      reload && this.$emit('reloadData')
    },

    handlePage (page) {
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    },

    activateLine (item) {
      return item.id === this.selectedItem || this.selectedItems.find(i => i === item.id)
    },

    handleClick ($event, item) {
      const cls = $event.target.classList

      if (
        !cls.contains('v-btn__content') &&
        !cls.contains('v-btn') &&
        !cls.contains('v-icon') &&
        !cls.contains('v-input--selection-controls__ripple') &&
        !cls.contains('accent--text')
      ) {
        this.selectedItem = item.companyGroup.id
        this.openGlobalDrawer({
          data: { ...item }
        })
      }
    },

    openRemoveCompanyGroupDialog (group) {
      this.companyGroup = group
      this.removeCompanyGroupDialog = true
    },

    closeRemoveCompanyGroupDialog (reload = false) {
      this.companyGroup = null
      this.removeCompanyGroupDialog = false
      if (reload) this.handleReloadData()
    },

    handleEditCompanyGroup (group) {
      this.$router.push({
        name: 'CompanyGroup',
        params: {
          type: 'editar',
          id: group.companyGroup.id
        }
      })
    },

    handleRemoveCompanyGroup (group) {
      this.openRemoveCompanyGroupDialog(group)
    }
  },

  watch: {
    activeTab () {
      this.closeGlobalDrawer()
    }
  }
}
</script>
